<script setup lang="ts">
import { useSectionTitle } from "@/composables/shared/useCompactScope";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  value?: string;
  editable?: boolean;
  multiline?: boolean;
  icon?: string;
  placeholder?: string;
  caption?: string;
  error?: boolean;
}>();

const { t } = useI18n();

const hasValue = computed(() => Boolean(props.value?.trim()));

const sectionTitle = useSectionTitle();

const visibleCaption = computed(() =>
  props.caption && props.caption.toLowerCase() !== sectionTitle?.value?.toLowerCase()
    ? props.caption
    : undefined
);

const isNote = computed(() => props.multiline && Boolean(visibleCaption.value));

const trailingIcon = computed(
  () => props.icon ?? (props.multiline ? "mdi-pencil-outline" : "mdi-chevron-down")
);
</script>

<template>
  <button
    v-if="isNote && (hasValue || editable)"
    v-bind="$attrs"
    type="button"
    :disabled="!editable"
    :title="value"
    :data-field-error="error || undefined"
    :class="[
      'tw:group tw:flex tw:w-full tw:items-start tw:gap-1.5 tw:rounded-md tw:!-mx-1 tw:!px-1 tw:!py-0.5 tw:text-left tw:text-xs tw:leading-5 tw:outline-none tw:transition-colors tw:duration-150',
      editable
        ? 'tw:cursor-pointer tw:hover:bg-on-surface/4 tw:focus-visible:bg-on-surface/4 tw:aria-expanded:bg-on-surface/4'
        : 'tw:cursor-default',
    ]"
    data-testid="compact-value-trigger"
  >
    <v-icon
      :icon="hasValue ? 'mdi-text-long' : 'mdi-plus'"
      size="14"
      :class="[
        'tw:shrink-0 tw:!mt-[3px]',
        hasValue ? 'tw:text-on-surface/40' : 'tw:text-secondary',
      ]"
    />

    <span
      v-if="hasValue"
      class="tw:min-w-0 tw:flex-1 tw:line-clamp-2 tw:text-on-surface/70"
    >
      <span class="tw:font-semibold tw:text-on-surface/80">{{ visibleCaption }}</span>
      · {{ value }}
    </span>

    <span
      v-else
      class="tw:font-semibold tw:text-secondaryDarker"
    >
      {{ t("generic.action.addEntity", { entity: visibleCaption }) }}
    </span>
  </button>

  <button
    v-else-if="!isNote"
    v-bind="$attrs"
    type="button"
    :disabled="!editable"
    :title="value"
    :data-field-error="error || undefined"
    :class="[
      'tw:group tw:flex tw:w-full tw:gap-2 tw:rounded-lg tw:border tw:bg-surface tw:!px-2.5 tw:text-left tw:text-[12.5px] tw:outline-none tw:transition-[border-color,box-shadow] tw:duration-150',
      multiline ? 'tw:items-start tw:!py-2' : 'tw:h-9.5 tw:items-center',
      error ? 'tw:border-error' : 'tw:border-on-surface/20',
      editable
        ? 'tw:cursor-pointer tw:hover:border-on-surface/40 tw:focus-visible:border-secondary tw:focus-visible:shadow-[0_0_0_3px_rgba(var(--v-theme-secondary),0.15)] tw:aria-expanded:border-secondary tw:aria-expanded:shadow-[0_0_0_3px_rgba(var(--v-theme-secondary),0.15)]'
        : 'tw:cursor-default tw:bg-on-surface/[0.03]',
    ]"
    data-testid="compact-value-trigger"
  >
    <span
      :class="[
        'tw:min-w-0 tw:flex-1',
        multiline ? 'tw:line-clamp-2 tw:whitespace-pre-line' : 'tw:truncate',
        hasValue ? 'tw:font-medium tw:text-on-surface' : 'tw:text-on-surface/45',
      ]"
    >
      {{ hasValue ? value : (placeholder ?? t("generic.states.notAnswered")) }}
    </span>

    <v-icon
      v-if="editable"
      :icon="trailingIcon"
      size="18"
      class="tw:shrink-0 tw:text-on-surface/45 tw:group-hover:text-on-surface/70"
    />
  </button>
</template>
